import type { PermissionCatalog, PermissionDomain } from "@closer/shared";
import { PERMISSION_DOMAIN_NAMES, PERMISSION_DOMAINS } from "@closer/shared";
import { Button, Checkbox, FormField, Input, Modal, Textarea, useToast } from "@closer/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, ApiRequestError } from "../../lib/apiClient";
import { tenantKey } from "../../lib/tenantQuery";

/**
 * Özel rol formu — oluşturma (CLOS-85), düzenleme (CLOS-86), çoğaltma (CLOS-87).
 *
 * ### ⚠ Seçilebilir yetkiyi SUNUCU söylüyor
 *
 * Katalog her girdi için `grantableToCustomRole` taşıyor ve form yalnız onu süzüyor. Risk
 * sınıfından kendi kuralını türetseydi (*"protected ise gizle"*) sunucudaki verme sınırıyla
 * sessizce ayrışırdı — kart: *"UI kendi permission anlamını üretmez."*
 *
 * ### ⚠ Riskli değişikliğin onayı KULLANICIDAN
 *
 * Sunucu riskli bir değişikliği 422 + `confirmRiskyChange` alan hatasıyla reddediyor. Form
 * bu reddi bir onay kutusuna çeviriyor ve **ancak kullanıcı işaretledikten sonra** yeniden
 * gönderiyor. Otomatik `true` göndermek, kartın *"kullanıcı onayı olmadan kaydedilmez"*
 * kriterini arayüzün sessizce çiğnemesi olurdu.
 *
 * ### Çoğaltmada yetki seçimi YOK
 *
 * `[030]` kararı: kaynak rolün seti sunucuda kopyalanıp güncel katalogla yeniden
 * değerlendiriliyor. Formda seçim sunmak, gönderilmeyen bir seçim göstermek — yani yalan —
 * olurdu.
 */

export type RoleFormMode =
  | { kind: "create" }
  | { kind: "edit"; role: RoleFormRole }
  | { kind: "duplicate"; role: RoleFormRole };

export type RoleFormRole = {
  id: string;
  name: string;
  description?: string | null;
  permissions: string[];
};

const BASARI: Record<RoleFormMode["kind"], string> = {
  create: "Rol oluşturuldu",
  edit: "Rol güncellendi",
  duplicate: "Rol çoğaltıldı",
};

export function RoleForm({
  tenantId,
  mode,
  catalog,
  onClose,
}: {
  tenantId: string;
  mode: RoleFormMode;
  catalog: PermissionCatalog | undefined;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const mevcut = mode.kind === "create" ? null : mode.role;
  const [ad, setAd] = useState(mode.kind === "duplicate" ? "" : (mevcut?.name ?? ""));
  const [aciklama, setAciklama] = useState(mevcut?.description ?? "");
  const [secili, setSecili] = useState<string[]>(mevcut?.permissions ?? []);
  const [onay, setOnay] = useState(false);
  const [onayIsteniyor, setOnayIsteniyor] = useState(false);
  const [hatalar, setHatalar] = useState<Record<string, string>>({});
  const [kaydediliyor, setKaydediliyor] = useState(false);

  /** Yalnız özel role VERİLEBİLEN yetkiler; karar katalogdan geliyor. */
  const verilebilir = (catalog?.entries ?? []).filter((e) => e.grantableToCustomRole);
  const gruplar = PERMISSION_DOMAINS.map((domain: PermissionDomain) => ({
    domain,
    entries: verilebilir.filter((e) => e.domain === domain),
  })).filter((g) => g.entries.length > 0);

  function degistir(key: string) {
    setSecili((onceki) =>
      onceki.includes(key) ? onceki.filter((k) => k !== key) : [...onceki, key],
    );
  }

  async function kaydet() {
    setKaydediliyor(true);
    setHatalar({});
    try {
      if (mode.kind === "duplicate") {
        await api(`/api/roles/${mode.role.id}/duplicate`, {
          method: "POST",
          body: JSON.stringify({ name: ad }),
          tenantId,
        });
      } else if (mode.kind === "edit") {
        await api(`/api/roles/${mode.role.id}`, {
          method: "PATCH",
          body: JSON.stringify({
            name: ad,
            ...(aciklama ? { description: aciklama } : {}),
            permissions: secili,
            confirmRiskyChange: onay,
          }),
          tenantId,
        });
      } else {
        await api("/api/roles", {
          method: "POST",
          body: JSON.stringify({
            name: ad,
            ...(aciklama ? { description: aciklama } : {}),
            permissions: secili,
          }),
          tenantId,
        });
      }

      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "roles-page", "false") });
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "roles-page", "true") });
      toast.success(BASARI[mode.kind]);
      onClose();
    } catch (error) {
      if (error instanceof ApiRequestError && error.fields) {
        const alanlar = Object.fromEntries(error.fields.map((f) => [f.field, f.message]));
        setHatalar(alanlar);
        /**
         * ⚠ Onay kutusu SUNUCUNUN reddiyle açılıyor: riskin ne olduğuna sunucu karar
         * veriyor (`isRiskyChange`). İstemci kendi kuralını türetseydi, arayüzü atlayan
         * bir istekte onay hiç sorulmazdı — ve arayüzde de yanlış zamanda sorulurdu.
         */
        if (alanlar.confirmRiskyChange) setOnayIsteniyor(true);
        return;
      }
      toast.error(error instanceof ApiRequestError ? error.message : "Kaydedilemedi.");
    } finally {
      setKaydediliyor(false);
    }
  }

  const baslik =
    mode.kind === "create"
      ? "Yeni özel rol"
      : mode.kind === "edit"
        ? `${mode.role.name} rolünü düzenle`
        : `${mode.role.name} rolünü çoğalt`;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={baslik}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Vazgeç
          </Button>
          <Button loading={kaydediliyor} onClick={() => void kaydet()}>
            Kaydet
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <FormField label="Rol adı" error={hatalar.name} required>
          <Input value={ad} onChange={(event) => setAd(event.target.value)} />
        </FormField>
        <FormField label="Açıklama" error={hatalar.description}>
          <Textarea
            rows={2}
            value={aciklama}
            onChange={(event) => setAciklama(event.target.value)}
          />
        </FormField>

        {mode.kind === "duplicate" ? (
          <p className="text-sm text-fg-muted">
            Kaynak rolün yetkileri kopyalanır ve güncel katalogla yeniden değerlendirilir.
          </p>
        ) : (
          <FormField label="Yetkiler" error={hatalar.permissions} required>
            <div className="flex flex-col gap-4">
              {gruplar.map((grup) => (
                <div key={grup.domain} className="flex flex-col gap-2">
                  <p className="text-xs font-medium tracking-wide text-fg-soft uppercase">
                    {PERMISSION_DOMAIN_NAMES[grup.domain]}
                  </p>
                  {grup.entries.map((girdi) => (
                    <Checkbox
                      key={girdi.key}
                      label={girdi.description}
                      checked={secili.includes(girdi.key)}
                      onChange={() => degistir(girdi.key)}
                    />
                  ))}
                </div>
              ))}
            </div>
          </FormField>
        )}

        {onayIsteniyor ? (
          <FormField label="Onay" error={hatalar.confirmRiskyChange} required>
            <Checkbox
              label="Değişikliği onaylıyorum"
              checked={onay}
              onChange={(event) => setOnay(event.target.checked)}
            />
          </FormField>
        ) : null}
      </div>
    </Modal>
  );
}
